/** @jsxImportSource @emotion/react */
import MuiMarkdown from "mui-markdown"
import { Box, Typography, Divider, Grid, Avatar, Icon, IconButton, Container, FormControl, Select, MenuItem, alpha, Backdrop, CircularProgress, CardMedia, Collapse, Button } from "@mui/material"
import Layout from "components/Layout"
import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"
import { CollectionPageStyle, CommonStyle } from "appearance"
import { useShoppingCart } from "use-shopping-cart"
import Theme from "appearance/Theme"
import { AxiosService } from "services/axios/AxiosService"
import useSWR from "swr"
import Image from "components/materials/Image"
import NftFullCard from "components/materials/NftFullCard"
import ExpandMore from "components/materials/ExpandMore"
import { updateProfileWallets } from "services/api/modules/profile"
import { useAccount, useNetwork, useSwitchNetwork } from "wagmi"
import { useWeb3Modal } from "@web3modal/react"
import { useSession } from "next-auth/react"
import { checkAuth } from "utils/AuthUtil"
import { EventEmitter } from "utils/EventUtil"
import { VenlyService } from "services/venly/VenlyService"
import { SecretType } from "@venly/connect"
import { formatAmountForStripe } from "utils/StripeUtil"

const CollectionPage = () => {
  const { cartDetails, addItem } = useShoppingCart()
  const router = useRouter()
  const { id } = router.query
  const [collection, setCollection] = useState<any>(null)
  const [media, setMedia] = useState<any>(null)
  const [nfts, setNfts] = useState<any[]>([])
  const [filteredNfts, setFilteredNfts] = useState<any[]>([])
  const [tiers, setTiers] = useState([])
  const [sortBy, setSortBy] = useState(0)
  const [filterBy, setFilterBy] = useState(-1)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const { data: session }: any = useSession()
  const [loading, setLoading] = useState(false)
  const { open: openWalletConnect } = useWeb3Modal()
  const { address, isConnected: accountConnected } = useAccount()
  const { chain } = useNetwork()
  const { switchNetwork } = useSwitchNetwork()
  const [coinbaseMATICReady, setCoinbaseMATICReady] = useState(false)
  const [addToCartNft, setAddToCartNft] = useState<any>(null)
  const [autoSwitchNetwork, setAutoSwitchNetwork] = useState(false)

  const { data } = useSWR(id ? `/api/collections/${id}` : null, AxiosService)

  if (data && !collection) {
    const collectionCryptoData = JSON.parse(data.crypto_data)
    setCollection({ ...collectionCryptoData, id: data.id, userId: data.userId })
    let collectionMedia = {}
    collectionCryptoData.media?.forEach(m => {
      collectionMedia[m.type] = m.value || ""
    })
    setMedia(collectionMedia)
    setNfts(data.nfts || [])
    setFilteredNfts(data.nfts || [])
    setTiers(data.commonTiers || [])
  }

  const onTierFilterChanged = value => {
    setFilterBy(value)
    if (value == -1) {
      setFilteredNfts(nfts)
    } else {
      setFilteredNfts(nfts.filter(x => x.tier.id == value))
    }
  }

  const sort = (sortBy: number) => {
    setSortBy(sortBy)
    switch (sortBy) {
      case 0:
        setFilteredNfts(filteredNfts.sort((x: any, y: any) => x.id - y.id))
        break
      case 1:
        setFilteredNfts(filteredNfts.sort((x: any, y: any) => x.price - y.price))
        break
      case 2:
        setFilteredNfts(filteredNfts.sort((x: any, y: any) => y.price - x.price))
        break
      default:
        break
    }
  }

  const handleDescriptionExpandClick = () => {
    setDescriptionExpanded(!descriptionExpanded)
  }

  const doAddToCart = useCallback(
    nft => {
      addItem({
        sku: nft.id,
        currency: "USD",
        name: nft.name,
        price: formatAmountForStripe(nft.price, "USD"),
        description: `Editions: ${nft.currentEdition} - Available: ${nft.currentAvailable}`,
        image: nft.image,
      })

      EventEmitter.emit("OpenPopup", {
        title: "Added to Cart",
        message: "This badge has been added to your cart.",
        status: "success",
        onOk: () => router.push("/cart"),
        okLabel: "Checkout Now",
        cancelLabel: "Continue Shopping",
      })
    },
    [addItem, router],
  )

  const createMaticWallet = useCallback(() => {
    if (process.browser) {
      VenlyService.flows.getAccount(SecretType.MATIC).then(account => {
        if (!account || account.wallets.filter(wallet => wallet.secretType === SecretType.MATIC).length === 0) return

        updateProfileWallets(account.wallets).then(() => {
          setCoinbaseMATICReady(true)
          if (addToCartNft) {
            doAddToCart(addToCartNft)
            setAddToCartNft(null)
          }
        })
      })
    }
  }, [addToCartNft, doAddToCart])

  const onAddToCart = useCallback(
    nft => {
      checkAuth(session).then(authorized => {
        if (authorized) {
          if (!coinbaseMATICReady) {
            setAddToCartNft(nft)

            EventEmitter.emit("OpenActionsPopup", {
              title: "Create or connect your wallet",
              message: "purchased badges will be stored here.",
              status: "info",
              actions: [
                {
                  label: "Create Wallet",
                  onClick: createMaticWallet,
                },
                {
                  label: "Connect existing wallet",
                  onClick: () => {
                    if (!accountConnected) {
                      openWalletConnect()
                      setAutoSwitchNetwork(true)
                    } else {
                      switchNetwork?.(137)
                    }
                  },
                },
              ],
            })
          } else {
            doAddToCart(nft)
          }
        }
      })
    },
    [session, coinbaseMATICReady, accountConnected, createMaticWallet, doAddToCart, openWalletConnect, switchNetwork],
  )

  useEffect(() => {
    if (autoSwitchNetwork && accountConnected) {
      switchNetwork?.(137)
      setAutoSwitchNetwork(false)
    }
  }, [autoSwitchNetwork, accountConnected, switchNetwork])

  useEffect(() => {
    const maticWallet = session?.wallets ? session?.wallets.map(x => JSON.parse(x.crypto_data)).find(x => x.secretType === "MATIC") : null
    const hasMaticWallet = !!maticWallet
    setCoinbaseMATICReady(hasMaticWallet)
  }, [session?.wallets])

  useEffect(() => {
    console.log("chain", chain)
    if (session && address && chain && !coinbaseMATICReady && !loading) {
      const chainId = chain.id
      if (chainId === 137) {
        setLoading(true)
        updateProfileWallets([{ address: `${address}`, secretType: "MATIC" }]).then(() => {
          setCoinbaseMATICReady(true)
          setLoading(false)
          if (addToCartNft) {
            doAddToCart(addToCartNft)
            setAddToCartNft(null)
          }
        })
      }
    }
  }, [session, address, chain, coinbaseMATICReady, addToCartNft, doAddToCart, loading])

  if (!data) {
    return (
      <Layout title="Tribly">
        <Backdrop sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Layout>
    )
  }

  return (
    <Layout title={collection?.name}>
      <Box
        css={CollectionPageStyle.FeaturedBoxStyle}
        sx={{ backgroundImage: `url('${collection?.media ? collection?.media.find(x => x.type === "cover")?.value : "https://via.placeholder.com/1420x260/212121?text=..."}')` }}
      />
      <Box css={CollectionPageStyle.CollectionInfoWrapperStyle}>
        <Container css={CollectionPageStyle.CollectionInfoGridStyle} maxWidth={false} sx={{ maxWidth: 1420 }}>
          <Grid container spacing={{ xs: 6 }}>
            <Grid item xs={12} sm={media && media["video"] ? 2 : 5} css={CollectionPageStyle.AvatarGridItemStyle} sx={{ justifyContent: { xs: "center", sm: "flex-end" } }}>
              <Avatar alt={collection?.name || ""} src={collection?.image || ""} sx={{ width: 188, height: 188 }} css={CollectionPageStyle.AvatarStyle} />
            </Grid>
            <Grid item xs={12} sm={media && media["video"] ? 5 : 7} sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography variant="h2">{collection?.name || "Unnamed"}</Typography>
              <Typography css={CommonStyle.ThreeDotsStyle}>...</Typography>
              <Collapse in={descriptionExpanded} collapsedSize={150}>
                <Box>
                  <Typography variant="subtitle1">
                    <MuiMarkdown
                      overrides={{
                        ul: {
                          props: {
                            style: {
                              color: "white",
                              fontFamily: "Poppins,Roboto",
                              fontSize: 14,
                            },
                          },
                        },
                      }}>
                      {collection?.description || ""}
                    </MuiMarkdown>
                  </Typography>
                  <br />
                </Box>
              </Collapse>
              <Box>
                <ExpandMore expand={descriptionExpanded} onClick={handleDescriptionExpandClick} aria-expanded={descriptionExpanded} aria-label="show more">
                  <Icon className="lnr-chevron-down" sx={{ fontSize: 12 }}></Icon>
                </ExpandMore>
              </Box>
              {/*  <Box sx={{ display: { xs: "flex", sm: "none" }, justifyContent: "center" }} mt={1}>
                {collection?.rewardsUrl && collection?.rewardsUrl.length > 0 && (
                  <Button href={collection.rewardsUrl} target="_blank">
                    Unlock Rewards
                  </Button>
                )}
              </Box> */}

              <Box css={CollectionPageStyle.CollectionInfoActionsStyle} sx={{ justifyContent: { xs: "center", md: "flex-start" }, alignItems: "center" }}>
                {/*  {collection?.rewardsUrl && collection?.rewardsUrl.length > 0 && (
                  <Button href={collection.rewardsUrl} target="_blank" sx={{ marginLeft: 0, marginRight: 2, display: { xs: "none", sm: "flex" } }}>
                    Unlock Rewards
                  </Button>
                )} */}
                <IconButton
                  aria-label="link"
                  css={CommonStyle.ActionGreyStyle}
                  sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                  href={collection?.externalUrl}
                  target="_blank">
                  <Icon className="lnr-link"></Icon>
                </IconButton>
                {media && media["facebook"] && (
                  <IconButton
                    aria-label="link"
                    css={CommonStyle.ActionGreyStyle}
                    sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                    href={media["facebook"]}
                    target="_blank">
                    <Image src="/assets/facebook-brands.svg" alt="facebook-brands" width={16} height={16} />
                  </IconButton>
                )}
                {media && media["instagram"] && (
                  <IconButton
                    aria-label="link"
                    css={CommonStyle.ActionGreyStyle}
                    sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                    href={media["instagram"]}
                    target="_blank">
                    <Image src="/assets/instagram-brands.svg" alt="instagram-brands" width={16} height={16} />
                  </IconButton>
                )}
                {media && media["tiktok"] && (
                  <IconButton
                    aria-label="link"
                    css={CommonStyle.ActionGreyStyle}
                    sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                    href={media["tiktok"]}
                    target="_blank">
                    <Image src="/assets/tiktok-brands.svg" alt="tiktok-brands" width={16} height={16} />
                  </IconButton>
                )}
                {media && media["twitter"] && (
                  <IconButton
                    aria-label="link"
                    css={CommonStyle.ActionGreyStyle}
                    sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                    href={media["twitter"]}
                    target="_blank">
                    <Image src="/assets/twitter-brands.svg" alt="twitter-brands" width={16} height={16} />
                  </IconButton>
                )}
                {media && media["youtube"] && (
                  <IconButton
                    aria-label="link"
                    css={CommonStyle.ActionGreyStyle}
                    sx={{ marginLeft: { xs: "6px", sm: 0 }, marginRight: { xs: "6px", sm: "12px" } }}
                    href={media["youtube"]}
                    target="_blank">
                    <Image src="/assets/youtube-brands.svg" alt="youtube-brands" width={16} height={16} />
                  </IconButton>
                )}
              </Box>
            </Grid>
            {media && media["video"] && (
              <Grid item xs={12} sm={5}>
                <CardMedia>
                  <video controls width={"100%"} height={250}>
                    <source src={media["video"]} type="video/mp4" />
                  </video>
                </CardMedia>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
      {/* <Box css={CollectionPageStyle.TiresWrapperStyle}>
        <Container css={CollectionPageStyle.TiresContainerStyle} maxWidth={false} sx={{ maxWidth: 1420 }}>
          <Typography variant="h2" sx={{ textAlign: "center", paddingLeft: { xs: 6, md: 0 }, paddingRight: { xs: 6, md: 0 } }}>
            Collection Tiers
          </Typography>
          <Grid container spacing={{ xs: 4 }} pt={4}>
            {tiers.map((tier: any) => (
              <Grid item key={tier.id} xs={12} md={12 / 5}>
                <TierPaper id={`${tier.id}`} name={tier.name} description={tier.description} image={tier.image} enabled={true} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}
      <Divider />
      <Container maxWidth={false} sx={{ maxWidth: 1420 }}>
        <Grid container css={CollectionPageStyle.SortWrapperStyle}>
          <Grid xs={12} sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" }, flexDirection: { xs: "column", md: "row" } }}>
            <FormControl sx={{ width: { xs: "100%", md: "auto" }, mr: { xs: 0, md: 2 } }}>
              <Select
                value={filterBy}
                onChange={e => onTierFilterChanged(e.target.value)}
                sx={{ minWidth: 198, height: 39, fontSize: 13, color: alpha("#FFFFFF", 0.5) }}
                IconComponent={() => <Icon className="lnr-chevron-down" sx={{ fontSize: 12, color: alpha("#FFFFFF", 0.5), margin: Theme.spacing(1) }} />}>
                <MenuItem key={-1} value={-1}>
                  {"All"}
                </MenuItem>
                {tiers.map((tier: any) => (
                  <MenuItem key={tier.id} value={tier.id}>
                    {tier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: { xs: "100%", md: "auto" }, mt: { xs: 2, md: 0 } }}>
              <Select
                value={sortBy}
                onChange={e => sort(parseInt(e.target.value.toString()))}
                sx={{ minWidth: 198, height: 39, fontSize: 13, color: alpha("#FFFFFF", 0.5) }}
                IconComponent={() => <Icon className="lnr-chevron-down" sx={{ fontSize: 12, color: alpha("#FFFFFF", 0.5), margin: Theme.spacing(1) }} />}>
                <MenuItem value={0}>Default Sorting</MenuItem>
                <MenuItem value={1}>Price: low-high</MenuItem>
                <MenuItem value={2}>Price: high-low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Container>
      <Divider />
      <Container maxWidth={false} sx={{ maxWidth: 1420 }}>
        <Grid container css={CollectionPageStyle.NftsGridStyle} spacing={{ xs: 3 }}>
          {filteredNfts
            .filter(x => x.enabled)
            .map((filteredNft: any) => {
              const nft = {
                ...JSON.parse(filteredNft.crypto_data),
                id: filteredNft.id,
                price: filteredNft.price,
                currentEdition: filteredNft.currentEdition,
                currentAvailable: filteredNft.currentAvailable,
                outOfStock: filteredNft.outOfStock,
                burned: filteredNft.burned,
                tier: filteredNft.tier,
                collection: collection,
                createdAt: new Date(filteredNft.createdAt).toDateString(),
              }
              return (
                <Grid item xs={12} md={6} key={nft.id}>
                  <NftFullCard inCart={Object.keys(cartDetails).indexOf(nft.id.toString()) >= 0} nft={nft} onAddToCart={() => onAddToCart(nft)} />
                </Grid>
              )
            })}
        </Grid>
      </Container>

      <Backdrop sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Layout>
  )
}

export default CollectionPage
